/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { AlertBanner, CustomToggleButton } from '@opengeoweb/shared';
import { Clock } from '@opengeoweb/theme';
import {
  TimelineBackground,
  TimelineTimeBar,
  WarningBlocksLayer,
} from './WarningsTimelineLayers';
import {
  getTimeFromX,
  getTimelineLayout,
  getTimelineX,
} from './warningsTimelineUtils';
import type { Warning } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import type { WarningListItemActions } from '../PublicWarningList/WarningListItemMenu';

export interface WarningsTimelineProps {
  warnings?: Warning[];
  hoveredWarningId?: string;
  onHoverWarning?: (warningId?: string) => void;
  onSelectWarning?: (warning: Warning) => void;
  selectedWarningId?: string;
  warningListItemActions?: WarningListItemActions;
}

const WARNING_BLOCK_HEIGHT = 34;
const DEFAULT_VISIBLE_ROWS = 10;
const TIMELINE_BAR_HEIGHT = 40;
const TIMELINE_BLOCK_TOP_GAP = 6;
const TIMELINE_SCROLLBAR_CLEARANCE = 16;
const MIN_TIMELINE_WIDTH = 350;

const MS_PER_HOUR = 60 * 60 * 1000;
const TIMESPANS = [24, 48, 72, 168, 336, 720] as const;
const TIMESPAN_KEYS = [
  'timeline-timespan-day',
  'timeline-timespan-two-days',
  'timeline-timespan-three-days',
  'timeline-timespan-week',
  'timeline-timespan-two-weeks',
  'timeline-timespan-month',
] as const;

export const WarningsTimeline: React.FC<WarningsTimelineProps> = ({
  warnings,
  hoveredWarningId,
  onHoverWarning,
  onSelectWarning,
  selectedWarningId,
  warningListItemActions,
}) => {
  const { t } = useWarningsTranslation();
  const { palette } = useTheme();
  const colors = palette.geowebColors;
  const [visibleHours, setVisibleHours] = React.useState<number>(48);
  const [timespanAnchor, setTimespanAnchor] =
    React.useState<HTMLElement | null>(null);
  const timespanMenuId = React.useId();
  const isTimespanOpen = Boolean(timespanAnchor);
  const timelineWarnings = React.useMemo(
    () =>
      warnings?.filter(
        (warning) =>
          Boolean(warning.warningDetail.validFrom) &&
          Boolean(warning.warningDetail.validUntil),
      ) ?? [],
    [warnings],
  );

  const warningsAreaRef = React.useRef<HTMLDivElement | null>(null);
  const timeAxisRef = React.useRef<HTMLDivElement>(null);
  const [scrollContainer, setScrollContainer] =
    React.useState<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = React.useState({
    width: 0,
    height: 0,
  });

  const handleWarningsAreaRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      warningsAreaRef.current = node;
      setScrollContainer(node);
    },
    [],
  );

  React.useEffect(() => {
    if (!warningsAreaRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nextSize = {
          width: warningsAreaRef.current?.clientWidth ?? 0,
          height: Math.round(entry.contentRect.height),
        };

        setContainerSize((currentSize) =>
          currentSize.width === nextSize.width &&
          currentSize.height === nextSize.height
            ? currentSize
            : nextSize,
        );
      }
    });

    resizeObserver.observe(warningsAreaRef.current);
    return (): void => resizeObserver.disconnect();
  }, []);

  const [now, setNow] = React.useState(() => new Date());
  const defaultTimelineStart = React.useMemo(
    () => new Date(Date.now() - 6 * MS_PER_HOUR),
    [],
  );
  const defaultTimelineRange = React.useMemo(
    () => ({
      end: new Date(
        defaultTimelineStart.getTime() + visibleHours * MS_PER_HOUR,
      ),
      start: defaultTimelineStart,
    }),
    [defaultTimelineStart, visibleHours],
  );
  const isApplyingScrollRef = React.useRef(false);
  const hasUserScrolledRef = React.useRef(false);
  const visibleStartTimeRef = React.useRef(defaultTimelineRange.start);

  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), MS_PER_HOUR / 60);
    return (): void => clearInterval(id);
  }, []);

  const viewportStart = hasUserScrolledRef.current
    ? visibleStartTimeRef.current
    : defaultTimelineRange.start;
  const { timelineStart, timelineEnd } = React.useMemo(() => {
    let warningMin = Number.POSITIVE_INFINITY;
    let warningMax = Number.NEGATIVE_INFINITY;

    for (const warning of timelineWarnings) {
      const { validFrom, validUntil } = warning.warningDetail;
      if (validFrom) {
        warningMin = Math.min(warningMin, new Date(validFrom).getTime());
      }
      if (validUntil) {
        warningMax = Math.max(warningMax, new Date(validUntil).getTime());
      }
    }

    const warningStartDate = new Date(
      Number.isFinite(warningMin)
        ? warningMin
        : defaultTimelineRange.start.getTime(),
    );
    warningStartDate.setUTCMinutes(0, 0, 0);
    const flooredHour =
      warningStartDate.getUTCHours() - (warningStartDate.getUTCHours() % 3) - 3;
    warningStartDate.setUTCHours(flooredHour);

    const minTime = Math.min(
      defaultTimelineRange.start.getTime(),
      timelineWarnings.length
        ? warningStartDate.getTime()
        : defaultTimelineRange.start.getTime(),
      viewportStart.getTime(),
    );
    // Changing the list must not discard the window the user is looking at.
    const maxTime = Math.max(
      defaultTimelineRange.end.getTime(),
      warningMax,
      viewportStart.getTime() + visibleHours * MS_PER_HOUR,
    );

    return {
      timelineEnd: new Date(maxTime),
      timelineStart: new Date(minTime),
    };
  }, [defaultTimelineRange, timelineWarnings, viewportStart, visibleHours]);

  const totalMs = timelineEnd.getTime() - timelineStart.getTime();
  const totalHours = totalMs / MS_PER_HOUR;
  // Use the scroll viewport itself, excluding the reserved scrollbar gutter.
  // Every pixel in the plot and axis represents the same amount of time.
  const isTooSmall = containerSize.width < MIN_TIMELINE_WIDTH;
  const viewportWidth = Math.max(1, containerSize.width);
  const layout = React.useMemo(
    () => getTimelineLayout(viewportWidth, visibleHours),
    [viewportWidth, visibleHours],
  );
  const timelineTopPadding = layout.headerHeight + TIMELINE_BLOCK_TOP_GAP;
  const contentWidth = (totalHours / visibleHours) * viewportWidth;
  const fullWidth = contentWidth;

  const setScrollPositionForTime = React.useCallback(
    (targetTime: Date): void => {
      if (!warningsAreaRef.current) {
        return;
      }

      const scrollLeft = Math.max(
        0,
        getTimelineX(targetTime, timelineStart, timelineEnd, contentWidth, 0),
      );
      const maxScrollLeft = Math.max(
        0,
        fullWidth - warningsAreaRef.current.clientWidth,
      );
      const nextScrollLeft = Math.min(scrollLeft, maxScrollLeft);

      isApplyingScrollRef.current = true;
      warningsAreaRef.current.scrollLeft = nextScrollLeft;
      visibleStartTimeRef.current = getTimeFromX(
        nextScrollLeft,
        timelineStart,
        timelineEnd,
        contentWidth,
        0,
      );
      if (timeAxisRef.current) {
        timeAxisRef.current.scrollLeft = nextScrollLeft;
      }
      window.requestAnimationFrame(() => {
        isApplyingScrollRef.current = false;
      });
    },
    [contentWidth, fullWidth, timelineEnd, timelineStart],
  );

  React.useLayoutEffect(() => {
    setScrollPositionForTime(
      hasUserScrolledRef.current
        ? visibleStartTimeRef.current
        : defaultTimelineRange.start,
    );
  }, [defaultTimelineRange.start, setScrollPositionForTime]);

  const handleWarningsScroll = React.useCallback(() => {
    if (!warningsAreaRef.current || !timeAxisRef.current) {
      return;
    }

    timeAxisRef.current.scrollLeft = warningsAreaRef.current.scrollLeft;
    visibleStartTimeRef.current = getTimeFromX(
      warningsAreaRef.current.scrollLeft,
      timelineStart,
      timelineEnd,
      contentWidth,
      0,
    );

    if (!isApplyingScrollRef.current) {
      hasUserScrolledRef.current = true;
    }
  }, [contentWidth, timelineEnd, timelineStart]);

  const timelineRowsHeight = Math.max(
    timelineWarnings.length * WARNING_BLOCK_HEIGHT + timelineTopPadding,
    DEFAULT_VISIBLE_ROWS * WARNING_BLOCK_HEIGHT + timelineTopPadding,
  );
  const timelineCanvasHeight = Math.max(
    containerSize.height,
    timelineRowsHeight + TIMELINE_SCROLLBAR_CLEARANCE,
  );

  const warningBlocks = React.useMemo(() => {
    return timelineWarnings.map((warning, index) => {
      const { warningDetail } = warning;
      const validFrom = new Date(warningDetail.validFrom as string);
      const validUntil = new Date(warningDetail.validUntil as string);

      const left = getTimelineX(
        validFrom,
        timelineStart,
        timelineEnd,
        contentWidth,
        0,
      );
      const right = getTimelineX(
        validUntil,
        timelineStart,
        timelineEnd,
        contentWidth,
        0,
      );

      return {
        key: warning.id ?? `${validFrom.toISOString()}-${index}`,
        left,
        top: timelineTopPadding + index * WARNING_BLOCK_HEIGHT,
        warning,
        width: Math.max(0, right - left),
      };
    });
  }, [
    contentWidth,
    timelineEnd,
    timelineStart,
    timelineWarnings,
    timelineTopPadding,
  ]);

  const [hoverTimelineX, setHoverTimelineX] = React.useState<number | null>(
    null,
  );

  const hoverTime =
    hoverTimelineX !== null
      ? `${getTimeFromX(
          hoverTimelineX,
          timelineStart,
          timelineEnd,
          contentWidth,
          0,
        ).toLocaleString('en-GB', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        })} UTC`
      : '';

  const handleTimeAxisMouseMove = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientY - rect.top < 10) {
        setHoverTimelineX(null);
        return;
      }

      setHoverTimelineX(
        event.clientX - rect.left + (timeAxisRef.current?.scrollLeft ?? 0),
      );
    },
    [],
  );

  const handleTimeAxisMouseLeave = React.useCallback(() => {
    setHoverTimelineX(null);
  }, []);

  const currentTimeX = getTimelineX(
    now,
    timelineStart,
    timelineEnd,
    contentWidth,
    0,
  );
  const showCurrentTime =
    now.getTime() >= timelineStart.getTime() &&
    now.getTime() <= timelineEnd.getTime();

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.surface,
      }}
    >
      {isTooSmall ? (
        <Box sx={{ padding: 1 }}>
          <AlertBanner
            info={t('timeline-view-too-small-description')}
            severity="warning"
            title={<strong>{t('timeline-view-too-small')}</strong>}
          />
        </Box>
      ) : null}
      <Box
        sx={{
          display: isTooSmall ? 'none' : 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 0.5,
          flexShrink: 0,
          py: 0.5,
          px: 0.5,
          paddingRight: 3,
          backgroundColor: colors.warningsTimeline.controls,
        }}
      >
        <Tooltip describeChild title={t('timeline-select-timespan')}>
          <CustomToggleButton
            aria-controls={isTimespanOpen ? timespanMenuId : undefined}
            aria-expanded={isTimespanOpen}
            aria-haspopup="menu"
            aria-label={t('timeline-timespan')}
            onClick={(event) => {
              event.stopPropagation();
              setTimespanAnchor(event.currentTarget);
            }}
            selected={isTimespanOpen}
            sx={{
              textTransform: 'initial!important',
              fontSize: '12px',
              height: '28px!important',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              padding: '0px 8px!important',
              ...(isTimespanOpen
                ? {
                    '&.MuiToggleButton-root.primary': {
                      backgroundColor:
                        'geowebColors.buttons.primary.mouseOver.fill',
                    },
                  }
                : {}),
            }}
          >
            <Clock data-testid="timeline-timespan-clock" />
            {t(
              TIMESPAN_KEYS[
                TIMESPANS.findIndex((hours) => hours === visibleHours)
              ],
            )}
          </CustomToggleButton>
        </Tooltip>
        <Menu
          anchorEl={timespanAnchor}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          id={timespanMenuId}
          onClose={() => setTimespanAnchor(null)}
          open={isTooSmall ? false : isTimespanOpen}
          slotProps={{
            list: { 'aria-label': t('timeline-timespan'), sx: { padding: 0 } },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <ListSubheader sx={{ fontSize: 12, lineHeight: '1.5', py: 1 }}>
            {t('timeline-timespan')}
          </ListSubheader>
          {TIMESPANS.map((hours, index) => (
            <MenuItem
              key={hours}
              onClick={(event) => {
                event.stopPropagation();
                setVisibleHours(hours);
                setTimespanAnchor(null);
              }}
              selected={hours === visibleHours}
            >
              {t(TIMESPAN_KEYS[index])}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <Box
        aria-hidden={isTooSmall}
        data-testid="warnings-timeline-scroll-container"
        onScroll={handleWarningsScroll}
        ref={handleWarningsAreaRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          scrollbarGutter: 'stable',
          // Keep measuring the viewport and preserve scrolling while hidden.
          visibility: isTooSmall ? 'hidden' : 'visible',
          minHeight: 0,
        }}
      >
        <Box
          data-testid="warnings-timeline-canvas"
          sx={{ width: fullWidth, position: 'relative' }}
        >
          <Box
            sx={{
              position: 'relative',
              height: timelineCanvasHeight,
            }}
          >
            <TimelineBackground
              contentWidth={contentWidth}
              layout={layout}
              scrollContainer={scrollContainer}
              timelineEnd={timelineEnd}
              timelineStart={timelineStart}
              visibleHours={visibleHours}
            />

            {showCurrentTime ? (
              <Box
                data-testid="timeline-current-time-line"
                sx={{
                  position: 'absolute',
                  left: currentTimeX,
                  top: layout.headerHeight,
                  bottom: 0,
                  width: '2px',
                  transform: 'translateX(-50%)',
                  bgcolor: colors.warningsTimeline.accent,
                  boxSizing: 'content-box',
                  borderLeft: `2px solid ${palette.common.white}`,
                  borderRight: `2px solid ${palette.common.white}`,
                  zIndex: 10,
                }}
              />
            ) : null}

            <WarningBlocksLayer
              hoveredWarningId={hoveredWarningId}
              onHoverWarning={onHoverWarning}
              onSelectWarning={onSelectWarning}
              selectedWarningId={selectedWarningId}
              warningBlocks={warningBlocks}
              warningListItemActions={warningListItemActions}
            />
          </Box>
        </Box>
        {showCurrentTime ? (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              height: 0,
              width: fullWidth,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <Box
              data-testid="timeline-current-time-dot"
              sx={{
                position: 'absolute',
                left: currentTimeX,
                bottom: 0,
                width: 20,
                height: 20,
                transform: 'translateX(-50%)',
                borderRadius: '50%',
                backgroundColor: colors.warningsTimeline.accent,
              }}
            />
          </Box>
        ) : null}
      </Box>

      <Tooltip
        enterDelay={150}
        followCursor
        leaveDelay={0}
        placement="top"
        slotProps={{
          popper: {
            sx: {
              pointerEvents: 'none',
            },
          },
        }}
        title={hoverTime}
      >
        <Box
          aria-hidden={isTooSmall}
          onMouseLeave={handleTimeAxisMouseLeave}
          onMouseMove={handleTimeAxisMouseMove}
          ref={timeAxisRef}
          sx={{
            height: TIMELINE_BAR_HEIGHT,
            visibility: isTooSmall ? 'hidden' : 'visible',
            flexShrink: 0,
            width: containerSize.width || '100%',
            overflow: 'hidden',
            borderTop: `1px solid ${colors.warningsTimeline.grid}`,
          }}
        >
          <TimelineTimeBar
            contentWidth={contentWidth}
            fullWidth={fullWidth}
            layout={layout}
            scrollContainer={scrollContainer}
            timelineEnd={timelineEnd}
            timelineStart={timelineStart}
            visibleHours={visibleHours}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};
